import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowRight, Calendar, CheckCircle2, CreditCard, TrendingUp } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { useB2BBillingStore } from '../../stores/useB2BBillingStore'

/**
 * B2B billing dashboard: current plan + usage progress + auto-renew controls.
 */
export default function BillingScreen() {
  const subscription = useB2BBillingStore((s) => s.subscription)
  const loading = useB2BBillingStore((s) => s.loadingSubscription)
  const error = useB2BBillingStore((s) => s.error)
  const loadSubscription = useB2BBillingStore((s) => s.loadSubscription)
  const cancelAutoRenew = useB2BBillingStore((s) => s.cancelAutoRenew)

  const [cancelStatus, setCancelStatus] = useState(null)
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    loadSubscription()
  }, [loadSubscription])

  const handleCancel = async () => {
    setCancelling(true)
    setCancelStatus(null)
    const result = await cancelAutoRenew()
    setCancelling(false)
    setCancelStatus(result)
  }

  if (loading && !subscription) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-4xl mx-auto">
          <p className="text-sm text-ink-3">Loading subscription…</p>
        </div>
      </AppShell>
    )
  }

  const plan = subscription?.plan
  const quota = subscription?.quota
  const used = quota?.used ?? 0
  const limit = quota?.limit ?? 0
  const isUnlimited = limit === -1
  const percent = isUnlimited || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100))
  const isPaid = plan && plan.plan_code !== 'free'

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-4xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Billing</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-bold text-ink-1">Plan & usage</h1>

        {error && !subscription && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {plan && (
          <>
            <div className="mt-8 bg-white rounded-3xl border border-black/5 p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Current plan</p>
                  <h2 className="mt-1 text-2xl font-bold text-ink-1">{plan.plan_name}</h2>
                  {plan.tagline && <p className="text-sm text-ink-3 mt-1">{plan.tagline}</p>}
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-ink-1">${Number(plan.price_usd).toFixed(0)}</p>
                  <p className="text-sm text-ink-3">{subscription.billing_period}</p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {subscription.expires_at && (
                  <Stat
                    icon={Calendar}
                    label="Expires"
                    value={fmtDate(subscription.expires_at)}
                  />
                )}
                {subscription.next_billing_date && (
                  <Stat
                    icon={CreditCard}
                    label="Next billing"
                    value={fmtDate(subscription.next_billing_date)}
                  />
                )}
                <Stat
                  icon={CheckCircle2}
                  label="Status"
                  value={subscription.status}
                  valueClass="capitalize text-emerald-600"
                />
                <Stat
                  icon={TrendingUp}
                  label="Auto-renew"
                  value={subscription.auto_renew ? 'On' : 'Off'}
                />
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/b2b/pricing"
                  className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition"
                >
                  {isPaid ? 'Change plan' : 'Upgrade'}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                {isPaid && subscription.auto_renew && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="inline-flex items-center gap-2 border border-black/10 bg-white hover:bg-canvas text-ink-1 font-bold px-5 py-2.5 rounded-2xl transition disabled:opacity-50"
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel auto-renewal'}
                  </button>
                )}
              </div>

              {cancelStatus?.ok && (
                <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  Auto-renewal cancelled. Your plan stays active until {fmtDate(subscription.expires_at)}.
                </div>
              )}
              {cancelStatus && cancelStatus.ok === false && (
                <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {cancelStatus.error}
                </div>
              )}
            </div>

            <div className="mt-6 bg-white rounded-3xl border border-black/5 p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-ink-3">
                Usage — {quota?.period === 'lifetime' ? 'Lifetime (free plan)' : quota?.period}
              </p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-bold text-ink-1">{used}</span>
                <span className="text-sm text-ink-3">
                  / {isUnlimited ? '∞' : limit} screenings
                </span>
              </div>

              {!isUnlimited && (
                <div className="mt-4 h-2 bg-canvas rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${percent >= 90 ? 'bg-red-500' : percent >= 75 ? 'bg-amber-500' : 'bg-accent'}`}
                    style={{ width: `${percent}%` }}
                  />
                </div>
              )}

              {!isUnlimited && limit > 0 && used >= limit && (
                <div className="mt-4 flex items-start gap-2 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p>
                    Quota reached. New screenings are blocked until you{' '}
                    <Link to="/b2b/pricing" className="font-bold underline">upgrade</Link>
                    {quota?.period !== 'lifetime' && ' or wait until next period'}.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}

function Stat({ icon, label, value, valueClass = '' }) {
  const IconCmp = icon
  return (
    <div className="rounded-2xl bg-canvas px-4 py-3">
      <div className="flex items-center gap-2 text-ink-3">
        <IconCmp className="w-4 h-4" />
        <p className="text-xs font-bold uppercase tracking-wider">{label}</p>
      </div>
      <p className={`mt-1 text-base font-semibold text-ink-1 ${valueClass}`}>{value}</p>
    </div>
  )
}

function fmtDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return iso
  }
}
